import React, { useEffect, useRef, useState } from "react";
import "../../../../../css/RecruitmentScreen/Sections.css";
import FormField from "../FormField";
import UseDatepicker from "../Datepicker";
import MobileNumberInput from "../../../../DesignSystem/Inputs/MobileNumberInput";
import { writeDataS5 } from "../../../../../backend/Sections/Section5/writeDataS5";
import { readDataS5 } from "../../../../../backend/Sections/Section5/readDataS5";
import errorTrackerV2 from "../Validation/Utils/ErrorTrackerV2";
import YesNoRadio from "../RadioButton";
import {
  addSubmissionListener,
  getIsSubmitted,
} from "../../Utils/submissionListener";
import { auth } from "../../../../../backend/firebaseConfig";
import { shouldBypassValidation } from "../../Utils/validationPolicy";
import { validateSection5 } from "../Validation/section5";
import InputField from "../../../../DesignSystem/Inputs/InputField";
import DateField from "../../../../DesignSystem/Inputs/DateField";
import AlertBanner from "../../../../DesignSystem/AlertBanner/AlertBanner";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";
import SearchInput from "../../../../DesignSystem/Inputs/SearchInput";
import {
  getAddressAutocomplete,
  getAddressById,
} from "../../Utils/Postcodes/getAddressClient";

const defaultTradeReferees = [
  {
    tradeName: "",
    companyName: "",
    tradeAddress: "",
    tradeEmailAddress: "",
    refereePhoneNumber: "",
    datesOfTradingActivities: "",
  },
  {
    tradeName: "",
    companyName: "",
    tradeAddress: "",
    tradeEmailAddress: "",
    refereePhoneNumber: "",
    datesOfTradingActivities: "",
  },
];

const defaultPersonalReferees = [
  {
    refereeName: "",
    refereeAddress: "",
    refereePhoneNumber: "",
    datesOfUnemployment: "",
    refereeEmailAddress: "",
    relationshipToReferee: "",
  },
  {
    refereeName: "",
    refereeAddress: "",
    refereePhoneNumber: "",
    datesOfUnemployment: "",
    refereeEmailAddress: "",
    relationshipToReferee: "",
  },
];

const coerceBool = (v) =>
  v === true || v === false ? v : String(v ?? "").toLowerCase() === "true";

const Section5 = ({ onValidationStatusChange }) => {
  const sectionTopRef = useRef(null);
  const [formData, setFormData] = useState({
    hasAccountant: null,
    title: "",
    surname: "",
    initials: "",
    telephone: "",
    emailAddress: "",
    address: "",
    from: "",
    tradeReferees: defaultTradeReferees,
    personalReferees: defaultPersonalReferees,
  });

  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(getIsSubmitted());
  const [refAddressSearch, setRefAddressSearch] = useState({});
  const [refAddrSuggestions, setRefAddrSuggestions] = useState({});
  const [refAddrDropdownOpen, setRefAddrDropdownOpen] = useState({});
  const [refAddrState, setRefAddrState] = useState({ state: "idle", msg: "" });
  const refAddrDebounceRef = useRef(null);
  const refAutoAbortRef = useRef(null);
  const refGetAbortRef = useRef(null);

  const cancelAddressRequest = (ref) => {
    try {
      ref.current?.abort?.();
    } catch {}
  };

  useEffect(() => {
    return () => {
      cancelAddressRequest(refAutoAbortRef);
      cancelAddressRequest(refGetAbortRef);
      if (refAddrDebounceRef.current) {
        clearTimeout(refAddrDebounceRef.current);
      }
    };
  }, []);

  const runRefAutocomplete = async (term, sectionName, index) => {
    const q = String(term || "").trim();

    if (q.length < 2) {
      const key = `${sectionName}-${index}`;
      setRefAddrSuggestions((prev) => ({ ...prev, [key]: [] }));
      setRefAddrDropdownOpen((prev) => ({ ...prev, [key]: false }));
      setRefAddrState({ state: "idle", msg: "" });
      return;
    }

    cancelAddressRequest(refAutoAbortRef);
    const ac = new AbortController();
    refAutoAbortRef.current = ac;

    setRefAddrState({ state: "loading", msg: "Searching addresses..." });

    const out = await getAddressAutocomplete(q, { signal: ac.signal });

    if (out?.aborted || ac.signal.aborted) return;

    const key = `${sectionName}-${index}`;

    if (!out.ok) {
      setRefAddrSuggestions((prev) => ({ ...prev, [key]: [] }));
      setRefAddrDropdownOpen((prev) => ({ ...prev, [key]: false }));
      setRefAddrState({
        state: "error",
        msg: out.error || "Autocomplete failed",
      });
      return;
    }

    const list = out.suggestions || [];

    setRefAddrSuggestions((prev) => ({
      ...prev,
      [key]: list,
    }));

    setRefAddrDropdownOpen((prev) => ({
      ...prev,
      [key]: list.length > 0,
    }));

    setRefAddrState({
      state: "success",
      msg: list.length ? `Found ${list.length} results` : "No results",
    });
  };

  const applyRefAddressFromId = async (id, sectionName, index) => {
    cancelAddressRequest(refGetAbortRef);
    const ac = new AbortController();
    refGetAbortRef.current = ac;

    setRefAddrState({ state: "loading", msg: "Fetching address..." });

    const out = await getAddressById(id, { signal: ac.signal });

    if (!out.ok) {
      setRefAddrState({
        state: "error",
        msg: out.error || "Failed to fetch address",
      });
      return;
    }

    const a = out.address || {};

    const addressField =
      sectionName === "tradeReferees" ? "tradeAddress" : "refereeAddress";

    const formattedAddress = [
      a.address || a.line_1 || "",
      a.line_2 || "",
      a.town_or_city || a.town || "",
      a.county || "",
      a.postcode || "",
    ]
      .filter(Boolean)
      .join(", ");

    setFormData((prev) => {
      const updated = [...(prev[sectionName] || [])];
      updated[index] = {
        ...(updated[index] || {}),
        [addressField]: formattedAddress,
      };

      return {
        ...prev,
        [sectionName]: updated,
      };
    });

    const key = `${sectionName}-${index}`;

    setRefAddressSearch((prev) => ({
      ...prev,
      [key]: a.postcode || a.address || a.line_1 || "",
    }));

    setRefAddrSuggestions((prev) => ({
      ...prev,
      [key]: [],
    }));

    setRefAddrDropdownOpen((prev) => ({
      ...prev,
      [key]: false,
    }));

    setRefAddrState({ state: "success", msg: "Address selected" });
  };

  // submission listener (stable deps)
  useEffect(() => {
    const remove = addSubmissionListener(setIsSubmitted);
    setIsSubmitted(getIsSubmitted());
    return () => remove();
  }, []);

  // load saved data
  useEffect(() => {
    (async () => {
      const data = await readDataS5();
      // console.log("Section 5: Data loaded from Firebase:", data);
      if (!data) {
        // console.log("Section 5: No data found");
        return;
      }
      // console.log(
      //   "Section 5: Setting form data, validation will trigger automatically",
      // );

      const fromDate = data.from?.seconds
        ? new Date(data.from.seconds * 1000)
        : "";

      setFormData((prev) => ({
        ...prev,
        ...data,
        hasAccountant:
          data.hasAccountant === null || data.hasAccountant === undefined
            ? null
            : coerceBool(data.hasAccountant),
        from: fromDate,
        tradeReferees:
          Array.isArray(data.tradeReferees) && data.tradeReferees.length
            ? data.tradeReferees
            : defaultTradeReferees,
        personalReferees:
          Array.isArray(data.personalReferees) && data.personalReferees.length
            ? data.personalReferees
            : defaultPersonalReferees,
      }));
      const nextTrade =
        Array.isArray(data.tradeReferees) && data.tradeReferees.length
          ? data.tradeReferees
          : defaultTradeReferees;

      const nextPersonal =
        Array.isArray(data.personalReferees) && data.personalReferees.length
          ? data.personalReferees
          : defaultPersonalReferees;

      const addressSearchMap = {};

      nextTrade.forEach((row, index) => {
        addressSearchMap[`tradeReferees-${index}`] = row.tradeAddress || "";
      });

      nextPersonal.forEach((row, index) => {
        addressSearchMap[`personalReferees-${index}`] =
          row.refereeAddress || "";
      });

      setRefAddressSearch(addressSearchMap);
    })();
  }, []);

  // single source validation
  const runValidation = (nextData) => {
    const nextErrors = validateSection5(nextData); // errors-only
    setErrors(nextErrors);

    const ok = nextErrors._isValid;

    // IMPORTANT: tracker stores ONLY error fields (no _isValid)
    errorTrackerV2.setSectionErrors("section5", nextErrors);
    // console.log("Validation errors for section5:", nextErrors._isValid);

    onValidationStatusChange?.(ok);
    return nextErrors._isValid;
  };

  useEffect(() => {
    runValidation(formData);
  }, [formData]);

  const isValid = Object.keys(errors).length === 0;

  // handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleNestedChange = (index, eOrValue, sectionName, fieldOverride) => {
    // supports both <input onChange> and PhoneInput onChange(value)
    const name = fieldOverride || eOrValue?.target?.name;
    const value = fieldOverride ? eOrValue : eOrValue?.target?.value;

    setFormData((prev) => {
      const updated = [...(prev[sectionName] || [])];
      updated[index] = { ...(updated[index] || {}), [name]: value };
      return { ...prev, [sectionName]: updated };
    });
  };

  const handleYesNoChange = (name, e) => {
    const raw = e?.target?.value;
    const bool =
      raw === true || raw === false
        ? raw
        : String(raw).toLowerCase() === "true";
    setFormData((prev) => ({ ...prev, [name]: bool }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowErrors(true);
    sectionTopRef.current?.scrollIntoView({ behavior: "smooth" });
    const ok = runValidation(formData);

    // Save data regardless of validation status
    writeDataS5(formData);

    // Show appropriate alert based on validation result
    // console.log("Form submission attempted. Validation status:", ok);
    if (ok) {
      setAlertType("success");
      setAlertMessage("Data saved successfully!");
    } else {
      setAlertMessage(
        "Data saved with errors. Please review the fields marked with errors.",
      );
      setAlertType("alert");
    }
  };

  const capitalizeFirstLetter = (s) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

  const tradeRefereeFields = [
    "tradeName",
    "companyName",
    "tradeAddress",
    "tradeEmailAddress",
    "refereePhoneNumber",
    "datesOfTradingActivities",
  ];

  const personalRefereeFields = [
    "refereeName",
    "refereeAddress",
    "refereePhoneNumber",
    "refereeEmailAddress",
    "relationshipToReferee",
    "datesOfUnemployment",
  ];

  const renderReferees = (referees, sectionName) => {
    const fieldOrder =
      sectionName === "personalReferees"
        ? personalRefereeFields
        : tradeRefereeFields;

    const singularLabel = (name) =>
      capitalizeFirstLetter(
        name
          .replace(/Referees$/, " Referee") // personalReferees -> personal Referee
          .replace(/([A-Z])/g, " $1") // camelCase -> spaced
          .trim(),
      );
    return (
      <div className="form-row">
        {referees.map((referee, index) => (
          <>
            <div className="Content">
              {singularLabel(sectionName)} {index + 1}
            </div>
            <div key={index} className="input-group input-group--grid-2col">
              {fieldOrder.map((field) => {
                const lookupKey = `${sectionName}-${index}`;
                const isAddressField =
                  field === "tradeAddress" || field === "refereeAddress";
                      if (isAddressField) {
                        return (
                          <div key={field} className="input-group">
                            <SearchInput
                              label="Address Lookup"
                              placeholder="Start typing address or postcode..."
                              value={refAddressSearch[lookupKey] || ""}
                              suggestions={
                                refAddrDropdownOpen[lookupKey]
                                  ? refAddrSuggestions[lookupKey] || []
                                  : []
                              }
                              onSuggestionSelect={(s) => {
                                const addressId =
                                  s?.global_address_key ||
                                  s?.id ||
                                  s?.value ||
                                  s?.addressId ||
                                  null;

                                if (addressId) {
                                  applyRefAddressFromId(
                                    addressId,
                                    sectionName,
                                    index,
                                  );
                                }
                              }}
                              onChange={(val) => {
                                setRefAddressSearch((prev) => ({
                                  ...prev,
                                  [lookupKey]: val,
                                }));

                                if (refAddrDebounceRef.current) {
                                  clearTimeout(refAddrDebounceRef.current);
                                }

                                refAddrDebounceRef.current = setTimeout(
                                  () =>
                                    runRefAutocomplete(val, sectionName, index),
                                  300,
                                );
                              }}
                              onSubmit={() =>
                                runRefAutocomplete(
                                  refAddressSearch[lookupKey] || "",
                                  sectionName,
                                  index,
                                )
                              }
                            />

                            <InputField
                              name={field}
                              placeholder={
                                sectionName === "tradeReferees"
                                  ? "Trade Address"
                                  : "Referee Address"
                              }
                              value={referee[field] || ""}
                              onChange={(e) =>
                                handleNestedChange(index, e, sectionName)
                              }
                              multiline
                              rows={4}
                              error={errors[`${field}_${index}_${sectionName}`]}
                            />
                          </div>
                        );
                      }

                if (field.toLowerCase().includes("phone")) {
                  return (
                    <MobileNumberInput
                      key={field}
                      id={`${field}-${index}-${sectionName}`}
                      value={referee[field] || ""}
                      onChange={(val) =>
                        handleNestedChange(index, val || "", sectionName, field)
                      }
                      error={errors[`${field}_${index}_${sectionName}`] || ""}
                    />
                  );
                }
          

                return (
                  <InputField
                    key={field}
                    name={field}
                    placeholder={
                      field.toLowerCase().includes("refereename") ||
                      field.toLowerCase().includes("tradename")
                        ? "Full Name"
                        : field.toLowerCase().includes("datesofunemployment")
                          ? "Dates Known"
                          : capitalizeFirstLetter(
                              field.replace(/([A-Z])/g, " $1").trim(),
                            )
                    }
                    value={referee[field] || ""}
                    onChange={(e) => handleNestedChange(index, e, sectionName)}
                    isTextArea={
                      field.toLowerCase().includes("dates") &&
                      !field.includes("Email")
                    }
                    error={errors[`${field}_${index}_${sectionName}`]}
                  />
                );
              })}
            </div>
          </>
        ))}
      </div>
    );
  };

  return (
    <form className="section-form" onSubmit={handleSubmit} ref={sectionTopRef}>
      {alertMessage && (
        <AlertBanner
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertMessage("")}
        />
      )}
      <h3>Section 5 - Applicant Details</h3>
      <div className="Content">
        5.1 Accountant details
        <span style={{ color: "var(--Error)" }}>*</span>
      </div>
      <div className="input-group-row">
        <div className="Content">
          Do you currently have an accountant?
          <span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <YesNoRadio
          name="hasAccountant"
          value={formData.hasAccountant}
          onChange={(e) => handleYesNoChange("hasAccountant", e)} // boolean stored
          error={errors.hasAccountant}
        />
      </div>

      {formData.hasAccountant === true && (
        <>
          <div className="input-group input-group--grid-2col">
            <div
              style={{ gap: "10px", flexDirection: "column", display: "flex" }}
            >
              <InputField
                name="title"
                placeholder="Title"
                value={formData.title}
                onChange={handleChange}
                error={showErrors && !!errors.title}
              />
              <InputField
                name="initials"
                placeholder="Forename"
                value={formData.initials}
                onChange={handleChange}
                error={showErrors && !!errors.initials}
              />
              <InputField
                name="surname"
                placeholder="Surname"
                value={formData.surname}
                onChange={handleChange}
                error={showErrors && !!errors.surname}
              />
            </div>
            <InputField
              name="address"
              placeholder="Address"
              value={formData.address}
              onChange={handleChange}
              error={showErrors && !!errors.address}
              multiline
              rows={4}
            />

            <InputField
              name="emailAddress"
              placeholder="Email Address"
              label={"Email Address"}
              value={formData.emailAddress}
              onChange={handleChange}
              error={showErrors && !!errors.emailAddress}
            />

            <MobileNumberInput
              id="telephone"
              label="Telephone"
              value={formData.telephone}
              onChange={(val) =>
                setFormData((prev) => ({ ...prev, telephone: val || "" }))
              }
              error={showErrors && !!errors.telephone}
            />
            {/* {errors.telephone ? (
              <div className="error-text">{errors.telephone}</div>
            ) : null} */}
          </div>

          <div className="input-group-row">
            <div className="Content">
              How long has the above been your accountant?
            </div>
            <div className="input-group">
              <DateField
                name="from"
                label="From"
                value={formData.from}
                onChange={(e) => {
                  const dateValue = e.target.value
                    ? new Date(e.target.value)
                    : "";
                  setFormData((prev) => ({ ...prev, from: dateValue }));
                }}
                error={showErrors && !!errors.from}
                helperText={showErrors ? errors.from : ""}
              />
            </div>
          </div>
        </>
      )}

      <div className="separator" />

      <div className="Content">
        5.2 Trade Referees <br />
        If you have been self-employed within the last 3 years in an industry
        which is not within the financial services sector, please provide the
        names and addresses of 2 Trade Referees who can verify your trading
        activities
      </div>
      {renderReferees(formData.tradeReferees, "tradeReferees")}

      <div className="separator" />

      <div className="Content-Bold-Underline">5.3 Personal Referees</div>
      <div className="Content">
        Please note these Personal Referees cannot be family members or
        individuals with a relationship to Rosemount.
        <br />
        If you have any gaps in employment within the last 3 years, please
        provide the names and addresses of 2 Personal Referees.
      </div>
      {renderReferees(formData.personalReferees, "personalReferees")}

      {!isSubmitted && <PrimaryButton label="SAVE" onClick={handleSubmit} />}
    </form>
  );
};

export default Section5;
